import React, { useMemo } from "react";
import { DocusignEnvelopeStatus } from "../../graphql/types";
import useFormatDate, { dateFormatter } from "../../hooks/useFormatDate";
import useTranslations from "../../hooks/useTranslations";
import UserAvatar from "../utils/avatars/UserAvatar";
import { Link } from "../wombat-ui";

import {
  Box,
  Tag,
} from "@chakra-ui/react";
import {
  ReviewFragment,
  ReviewReportFullFragment,
  UserMinimalFragment,
} from "../../graphql/generated/definitions";
import useDateFnsLocale from "../../hooks/useDateFnsLocale";
import {
  CheckCircleIcon,
  DotsCircleHorizontalIcon,
} from "@heroicons/react/outline";
import { compact } from "lodash";
import TimelinePopover from '../utils/timeline/TimelinePopover';
import TimelineRow from '../utils/timeline/TimelineRow';

type ReportPlateProps = {
  review: ReviewFragment;
  report: ReviewReportFullFragment;
  user: UserMinimalFragment;
  showSignature: boolean;
};

function getSignatureTagVariant(status: DocusignEnvelopeStatus): string {
  switch (status) {
    case "draft":
    case "sent":
    case "created":
    case "delivered":
    case "processing":
    case "signed":
      return "secondary";
    case "declined":
    case "deleted":
    case "voided":
      return "danger";
    case "completed":
      return "success";
  }
}

const ReportPlate: React.FC<ReportPlateProps> = ({
  review,
  report,
  user,
  showSignature = true,
}) => {
  const t = useTranslations();

  const dateFnsLocale = useDateFnsLocale();

  const formattedCreatedAt = useFormatDate({
    date: report.createdAt,
    format: "short",
  });
  const formattedFilledAt = useFormatDate({
    date: report.filledAt,
    format: "short",
  });
  const formattedCheckedAt = useFormatDate({
    date: report.checkedAt,
    format: "short",
  });

  const reportStatuses = useMemo(() => {
    return compact([
      {
        title: t("reports.created") as string,
        date: formattedCreatedAt,
        done: true,
      },
      {
        title: t("reports.filled", { by: review.collaborator.name }) as string,
        date: formattedFilledAt,
        done: !!report.filledAt,
        tag: t("reviews.collaborator") as string,
      },
      ...(report.filledAt
        ? report.reportReferents.map((reportReferent) => ({
            title: reportReferent.filledAt
              ? (t("reports.filled", {
                  by: reportReferent.referent.name,
                }) as string)
              : (t("reviews.waitingForReferent", {
                  referent: reportReferent.referent.name,
                }) as string),
            date:
              reportReferent.filledAt &&
              dateFormatter(
                reportReferent.filledAt,
                t(`dateFormat.short` as const) as string,
                t("dateFormat.fallback") as string,
                dateFnsLocale
              ),
            tag:
              reportReferent.filledAt &&
              (t("reviews.referent", { count: 0 }) as string),
            done: !!reportReferent.filledAt,
          }))
        : []),
      report.reportReferents.every(
        (reportReferent) => !!reportReferent.filledAt
      ) && {
        title: t("reports.checked") as string,
        date: formattedCheckedAt,
        done: !!report.checkedAt,
      },
    ]);
  }, [report]);

  return (
    <tr id={`report_plate_${report.id}`}>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <UserAvatar
            size={10}
            src={user.avatar}
            alt={`${user.name} profile picture`}
          />
          <div className="ml-4">
            <div className="text-sm font-medium text-dark-100">{user.name}</div>
            <div className="text-sm text-light-900">{user.email}</div>
          </div>
        </div>
      </td>
      {showSignature && (
        <td className="px-6 py-4 whitespace-nowrap">
          {report.docusignEnvelopeStatus && (
            <Tag
              variant={getSignatureTagVariant(report.docusignEnvelopeStatus)}
            >
              {t(
                `reports.signature.shortStatus.${report.docusignEnvelopeStatus}` as const
              )}
            </Tag>
          )}
        </td>
      )}
      <td className="px-6 py-4 whitespace-nowrap">
        <TimelinePopover
          triggerComponent={
            <Tag
              as="button"
              variant="solid"
              colorScheme={report.checkedAt ? "blue" : undefined}
            >
              {report.checkedAt
                ? (t("reports.completedOn", {
                    time: (
                      <time dateTime={report.checkedAt} className='ml-1'>
                        {formattedCheckedAt}
                      </time>
                    ),
                  }) as string)
                : (t("reports.ongoing", {
                    time: (
                      <time dateTime={report.createdAt} className='ml-1'>
                        {formattedCreatedAt}
                      </time>
                    ),
                  }) as string)}
            </Tag>
          }
          items={reportStatuses}
        >
          {({ title, date, done, tag, index }) => (
            <TimelineRow
              title={title}
              description={
                tag ? (
                  <Box>
                    {date} &middot; <Tag size="sm">{tag}</Tag>
                  </Box>
                ) : (
                  date
                )
              }
              icon={done ? CheckCircleIcon : DotsCircleHorizontalIcon}
              iconColor={done ? "green.500" : ""}
              key={index}
              isLast={index === reportStatuses.length - 1}
            />
          )}
        </TimelinePopover>
      </td>
      <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
        <Link
          to={`/reports/${report.id}`}
          className="text-primary-600 hover:text-primary-900"
        >
          {t("reviews.reportsSection.view")}
        </Link>
      </td>
    </tr>
  );
};

export default React.memo(ReportPlate);
